import { Menu, MenuItem, Checkbox, ClickAwayListener, Button } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SearchBar } from './SearchBar';
import { useMemo, useState } from 'react';
import { useStyles } from './styles/MultiSelectFilter.styles';
import { Virtualize } from './Virtualize';

type MultiSelectValue = { [key: string]: any } | string;

interface MultiSelectFilterProps<Value extends MultiSelectValue> {
    label: string;
    idKey?: string;
    search?: boolean;
    disabled?: boolean;
    options: Value[];
    value: { [key: string]: boolean };
    onChange: (option: string) => void;
    getLabelValue: (option: Value) => string;
    sort?: boolean;
    margin?: boolean;
}

export const MultiSelectFilter = <Value extends MultiSelectValue>({
    label,
    idKey,
    disabled,
    search,
    options,
    value,
    onChange,
    getLabelValue,
    sort = true,
    margin = true,
}: MultiSelectFilterProps<Value>) => {
    const classes = useStyles();

    const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(null);
    const [searchText, setSearchText] = useState('');
    const [root, setRoot] = useState<HTMLDivElement | null>(null);

    const selected = options.filter((option) => value[idKey ? (option as any)[idKey] : option]);

    const filtered = useMemo(() => {
        const text = searchText.toLowerCase().trim();

        return options.filter((option) => {
            const name = getLabelValue(option).toLowerCase();
            return name.includes(text);
        });
    }, [searchText, options]);

    const sorted = useMemo(() => {
        // Don't bother with sorting logic if the feature isn't enabled
        if (!sort) return filtered;

        return [...filtered].sort((option) => {
            if (value[idKey ? (option as any)[idKey] : option]) return -1;
            return 0;
        });
    }, [sort, filtered, dropdownAnchor]);

    const setDialog = (anchorEl: HTMLElement | null) => {
        setDropdownAnchor(anchorEl);
    };

    return (
        <div style={margin ? { marginLeft: '10px' } : undefined}>
            <Button
                disabled={disabled}
                className={classes.button}
                onClick={(e) => {
                    setDialog(dropdownAnchor ? null : e.currentTarget);
                }}
                endIcon={<KeyboardArrowDownIcon className={classes.buttonIcon} />}
                variant={'outlined'}>
                {selected.length ? `${label}: ${selected.map(getLabelValue).join(',')}` : label}
            </Button>

            <Menu
                ref={(elem) => setRoot(elem)}
                open={Boolean(dropdownAnchor)}
                anchorEl={dropdownAnchor}
                sx={{ minWidth: '200px', maxHeight: '500px' }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <ClickAwayListener onClickAway={() => setDialog(null)}>
                    <div style={{ textAlign: 'center' }}>
                        {search && (
                            <div style={{ paddingBottom: '10px' }}>
                                <SearchBar
                                    value={searchText}
                                    width={150}
                                    onSearch={(text) => {
                                        setSearchText(text);
                                    }}
                                />
                            </div>
                        )}

                        {(sorted.length === 0 || disabled) && (
                            <MenuItem
                                sx={{
                                    width: '200px',
                                    justifyContent: 'center',
                                }}
                                disableRipple>
                                No Items Found
                            </MenuItem>
                        )}

                        {disabled
                            ? null
                            : (sort ? sorted : filtered).map((option, index) => (
                                  <Virtualize
                                      // Don't virtualize the first 10 items
                                      ignore={index < 9}
                                      root={root}
                                      key={`${idKey ? (option as any)[idKey] : ''} - ${getLabelValue(
                                          option
                                      )} - menuitem - ${value[idKey ? (option as any)[idKey] : option]}`}>
                                      <MenuItem
                                          onClick={() => onChange(idKey ? (option as any)[idKey] : option)}
                                          sx={{ width: '200px', whiteSpace: 'normal' }}
                                          disableRipple>
                                          <Checkbox
                                              size={'small'}
                                              disableRipple
                                              checked={value[idKey ? (option as any)[idKey] : option]}
                                          />
                                          {getLabelValue(option)}
                                      </MenuItem>
                                  </Virtualize>
                              ))}
                    </div>
                </ClickAwayListener>
            </Menu>
        </div>
    );
};
